import React, { useEffect, useState } from "react";

import { ChannelSelectors, Checkbox, MainTitle } from "./PreviewArea.styled";
import { SidebarChannelsText } from "../../SideBarChannels/SideBarChannels.styled";
import { channelsByShortName } from "../../../../../data/app__channels";
import {
  MEDIA_ASSET_CHANNELS,
  VERTICAL_MEDIA_ASSET_CHANNELS,
  VERTICAL_TEMPLATES
} from "../constants";
import { XIcon } from "../../../../../Components/Icons";
import { Button } from "../../../../../Components/Button";
import { Group } from "../../../../../Components/Layout";
import { ActionsMediaAssetsLab } from "../../../../../Actions/ActionsMediaAssetsLab/ActionsMediaAssetsLab";
import { useTranslation } from "react-i18next";
import { MediaAssetsLabStore, PreviewStore, withRefluxStore } from "../../../../../Stores";
import { useTemplateDimensions } from "./useTemplateDimensions";
import {
  isImageCloudinary,
  removeCloudinaryParams,
  updateCloudinaryUrlParams
} from "../layout/Layout";
import { allTemplates, TEMPLATES_ORIENTATIONS } from "./PreviewArea";
import { getImageDimensions } from "../../utils";
import { Tooltip } from "../../../../../Components/Overlays";

const ControlPanel = ({
  isReleased,
  campaignId,
  previewInputProps,
  // Store
  selectedTemplates,
  userProfile,
  posts,
  released
}) => {
  const { t } = useTranslation();
  const [selectedChannels, setSelectedChannels] = useState({});
  const [hasCroppedAll, setHasCroppedAll] = useState(false);

  useEffect(() => {
    setHasCroppedAll(false);
  }, [previewInputProps.backgroundImage, previewInputProps.video, previewInputProps.imageUrl]);

  const dimensions = useTemplateDimensions();
  const isCloudinaryImage = isImageCloudinary(previewInputProps.backgroundImage);

  const adPosts = isReleased ? released.posts : posts;
  const currentPosts = Object.keys(adPosts || {})
    .filter((key) => MEDIA_ASSET_CHANNELS.includes(key))
    .reduce((acc, key) => {
      acc[key] = adPosts[key];
      return acc;
    }, {});

  const allChannelsSelected = Object.keys(currentPosts || {}).every(
    (channelKey) => selectedChannels[channelKey]?.length === currentPosts[channelKey]?.length
  );

  const hasNoChannels = Object.entries(selectedChannels).length === 0;
  const hasNoTemplates = selectedTemplates.length === 0;

  const isValid = (key) =>
    !(
      selectedTemplates.some((template) => VERTICAL_TEMPLATES.includes(template)) &&
      !VERTICAL_MEDIA_ASSET_CHANNELS.includes(key)
    );

  useEffect(() => {
    const updatedChannels = { ...selectedChannels };
    let hasChanges = false;

    Object.keys(selectedChannels).forEach((key) => {
      if (!isValid(key)) {
        delete updatedChannels[key];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setSelectedChannels(updatedChannels);
    }
  }, [selectedTemplates, selectedChannels]);

  const applySelectedTemplates = () => {
    ActionsMediaAssetsLab.generateMediaAssets({
      selectedChannels,
      userName: userProfile?.userName,
      campaignId,
      existingPosts: currentPosts,
      isReleased
    });
  };

  const clearAllTemplates = () => {
    ActionsMediaAssetsLab.selectTemplate([]);
  };

  const updateCroppedImage = ({ updatedImage, key, w, h, cloudAr }) => {
    if (hasCroppedAll) {
      setHasCroppedAll(false);
      ActionsMediaAssetsLab.changeTemplateInputProps({ key: "isCropped", value: false });
      updatedImage = removeCloudinaryParams(previewInputProps[key]);
    } else {
      setHasCroppedAll(true);
      ActionsMediaAssetsLab.changeTemplateInputProps({ key: "isCropped", value: true });
      updatedImage = updateCloudinaryUrlParams({ url: previewInputProps[key], w, h, ar: cloudAr });
    }

    return updatedImage;
  };

  const handleAutoCropAll = async () => {
    const { height: originalHeight, width: originalWidth } = await getImageDimensions(
      previewInputProps.backgroundImage
    );

    Object.keys(allTemplates).forEach((templateKey) => {
      let updatedImage;
      let key = "backgroundImage";
      let currentTemplateKey = templateKey;

      if (templateKey === "RawVideo") {
        key = "video";

        TEMPLATES_ORIENTATIONS.forEach((orientation) => {
          const { width, cloudAr, height } =
            dimensions[templateKey][orientation] || dimensions.default;

          currentTemplateKey = `RawVideo${orientation}`;

          updatedImage = updateCroppedImage({
            updatedImage: previewInputProps[key],
            key,
            w: originalWidth || width,
            h: originalHeight || height,
            cloudAr
          });

          ActionsMediaAssetsLab.changeTemplateInputProps({
            templateKey: currentTemplateKey,
            key,
            value: updatedImage
          });
        });
      } else {
        const { width, cloudAr, height } = dimensions[templateKey] || dimensions.default;

        updatedImage = updateCroppedImage({
          updatedImage: previewInputProps[key],
          key,
          w: originalWidth || width,
          h: originalHeight || height,
          cloudAr
        });

        ActionsMediaAssetsLab.changeTemplateInputProps({
          templateKey: currentTemplateKey,
          key,
          value: updatedImage
        });
      }
    });
  };

  const handleAllCheckboxChange = () => {
    if (allChannelsSelected) setSelectedChannels({});
    else {
      const allChannels = Object.keys(currentPosts).reduce((acc, channelKey) => {
        acc[channelKey] = currentPosts[channelKey].map(({ postNumber }) => postNumber);
        return acc;
      }, {});

      setSelectedChannels(allChannels);
    }
  };

  const handleCheckboxChange = ({ channelKey, postNumber = null }) => {
    setSelectedChannels((prevSelectedChannels) => {
      const updatedChannels = { ...prevSelectedChannels };

      if (postNumber != null) {
        if (!updatedChannels[channelKey]) {
          updatedChannels[channelKey] = [];
        }
        const index = updatedChannels[channelKey].indexOf(postNumber);
        if (index > -1) {
          updatedChannels[channelKey] = updatedChannels[channelKey].filter(
            (num) => num !== postNumber
          );
        } else {
          updatedChannels[channelKey].push(postNumber);
        }

        if (updatedChannels[channelKey].length === 0) {
          delete updatedChannels[channelKey];
        }
      } else {
        if (
          updatedChannels[channelKey] &&
          updatedChannels[channelKey].length === currentPosts[channelKey].length
        ) {
          delete updatedChannels[channelKey];
        } else {
          updatedChannels[channelKey] = currentPosts[channelKey].map(
            ({ postNumber }) => postNumber
          );
        }
      }

      return updatedChannels;
    });
  };

  return (
    <>
      <Group align="center" justify="space-between" margin="0 0 1rem">
        <MainTitle>{t("Preview Area")}</MainTitle>
        <Button
          variant="secondary"
          size="small"
          onClick={() => ActionsMediaAssetsLab.toggleModal()}
          icon={<XIcon />}
          text={t("Close Lab")}
        />
      </Group>

      <hr style={{ width: "100%" }} />

      <Group gap="15px" margin="25px auto" justify="space-between">
        <Group direction="column" width="auto" gap="15px">
          <Group align="flex-end" width="auto">
            <SidebarChannelsText lineHeight="">{t("Asset Controls")}</SidebarChannelsText>
            {hasNoTemplates ? null : (
              <Button text={t("Clear selected")} variant="link" onClick={clearAllTemplates} />
            )}
          </Group>
          <Group direction="row" gap="5px">
            <Button
              disabled={hasNoChannels || hasNoTemplates}
              text={`${t("Apply selected")} (${selectedTemplates.length})`}
              onClick={applySelectedTemplates}
            />
            <Tooltip label={t("The selected image cannot be cropped")} enabled={!isCloudinaryImage}>
              <Button
                text={hasCroppedAll ? t("Reset all") : t("Auto crop all")}
                variant="secondary"
                onClick={handleAutoCropAll}
                disabled={!isCloudinaryImage}
              />
            </Tooltip>
          </Group>
        </Group>

        <Group direction="column" width="auto" gap="15px">
          <Group align="flex-end" width="auto">
            <SidebarChannelsText lineHeight="">
              {t("Selected Channels")} ({Object.entries(selectedChannels).length})
            </SidebarChannelsText>
            <Button
              variant="link"
              onClick={handleAllCheckboxChange}
              text={allChannelsSelected ? t("Clear All") : t("Select all")}
            />
          </Group>

          <Group gap="10px 5px" align="flex-start" style={{ flexWrap: "wrap" }}>
            {Object.entries(currentPosts).map(([key, postList]) => {
              // Only allow templates for channels that are in MEDIA_ASSET_CHANNELS
              const channelAllowTemplates = MEDIA_ASSET_CHANNELS.includes(key);
              if (!channelAllowTemplates) return null;

              const isChannelSelected = selectedChannels[key]?.length === postList?.length;

              return (
                <ChannelSelectors key={key}>
                  <Tooltip
                    label={t(
                      "The selected templates are not applicable to the {{channel}} channel",
                      { channel: key }
                    )}
                    enabled={!isValid(key)}
                  >
                    <Checkbox htmlFor={key} checked={isChannelSelected} isValid={isValid(key)}>
                      <input
                        id={key}
                        type="checkbox"
                        checked={isChannelSelected || false}
                        disabled={!isValid(key)}
                        onChange={(ev) => handleCheckboxChange({ ev, channelKey: key })}
                      />
                      <img src={`/images/icons-channels/${key}.svg`} />
                      {channelsByShortName[key].channel_full_name}
                    </Checkbox>
                  </Tooltip>

                  {postList?.length > 1 ? (
                    <Group direction="column" gap="1px">
                      {postList
                        .sort((a, b) => a.postNumber - b.postNumber)
                        .map(({ postNumber }) => {
                          const isPostSelected = selectedChannels[key]?.includes(postNumber);

                          return (
                            <Checkbox
                              htmlFor={`post-${key}-${postNumber}`}
                              key={`${key}-${postNumber}`}
                              checked={isPostSelected}
                              isValid={isValid(key)}
                              isPostBox
                            >
                              <input
                                id={`post-${key}-${postNumber}`}
                                type="checkbox"
                                checked={isPostSelected || false}
                                disabled={!isValid(key)}
                                onChange={(ev) =>
                                  handleCheckboxChange({ ev, channelKey: key, postNumber })
                                }
                              />
                              {t("Post")} {postNumber}
                            </Checkbox>
                          );
                        })}
                    </Group>
                  ) : null}
                </ChannelSelectors>
              );
            })}
          </Group>
        </Group>
      </Group>
    </>
  );
};

export default withRefluxStore(
  ControlPanel,
  [MediaAssetsLabStore, PreviewStore],
  ["posts", "released", "userProfile", "selectedTemplates"]
);
